import Async from "components/DesignSystem/AsyncComponents/Async";
import { Avatar } from "components/DesignSystem/AvatarGroup/Avatar";
import { Button } from "components/DesignSystem/Button/Button";
import { LockedButton } from "components/DesignSystem/Button/LockedButton";
import Card from "components/DesignSystem/Card/Card";
import { InfoItem } from "components/DesignSystem/InfoItem/InfoItem";
import Slider from "components/DesignSystem/Slider/Slider";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { Divider } from "components/design/Divider";
import { MyProducts as ProductAndServicesIcon } from "components/icons/LeftNav/Books/MyProducts";
import { SplitIcon } from "components/icons/SplitIcon";
import {
  PRICE_FREQUENCY_LABEL,
  PriceFrequency,
} from "constants/productPriceFrequency";
import {
  REVENUE_DATA_SOURCE,
  REVENUE_DATA_SOURCE_ICON,
  REVENUE_DATA_SOURCE_OPTIONS,
} from "constants/revenueDataSource";
import { ITEM_TYPES } from "constants/productAndServices";
import { PRODUCT_SERVICE_ITEM_TYPE } from "dictionaries";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { LockedUi } from "components/LockedUi/LockedUi";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { useModal } from "hooks/useModal";
import { useParams } from "react-router-dom";
import { useGetProductAndServiceByIdQuery } from "store/apis/productAndServices";
import {
  LinkedPriceItem,
  ProductAndServicesDetail,
} from "types/Models/productAndServices";
import { ProductFormModalRoot } from "./ProductFormModal";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { Link } from "components/icons/Link";
import { Contacts } from "components/icons/LeftNav/Books/Contacts";

type RevenueDataSource =
  (typeof REVENUE_DATA_SOURCE)[keyof typeof REVENUE_DATA_SOURCE];

const SourceDisplay = ({ source }: { source: RevenueDataSource }) => {
  if (source === REVENUE_DATA_SOURCE.MANUAL) {
    return (
      <div className="t-flex t-gap-2 t-items-center">
        <span
          className="t-rounded-full t-bg-white t-overflow-hidden t-min-h-6 t-min-w-6 t-max-h-6 t-max-w-6 t-text-neutral-40 t-border-neutral-0 t-border t-border-solid t-text-center"
          role="img"
        >
          <Contacts />
        </span>
        {REVENUE_DATA_SOURCE_OPTIONS[source]}
      </div>
    );
  }

  return (
    <div className="t-flex t-gap-2 t-items-center">
      <Avatar
        size="small"
        src={REVENUE_DATA_SOURCE_ICON[source]}
        alt={source}
      />
      {REVENUE_DATA_SOURCE_OPTIONS[source]}
    </div>
  );
};

const PriceCard = ({
  priceItem,
  isDefault,
}: {
  priceItem: LinkedPriceItem;
  isDefault: boolean;
}) => {
  const frequencyLabel =
    PRICE_FREQUENCY_LABEL[priceItem.frequency as PriceFrequency] ||
    priceItem.frequency;

  return (
    <div className="t-border t-border-solid t-border-neutral-10 t-rounded-lg t-p-3 t-flex t-flex-col t-gap-1">
      <div className="t-flex t-items-center t-justify-between">
        <span className="t-text-subtitle-sm">
          <AmountSuperScript amount={priceItem.price} />
        </span>
        {isDefault && (
          <Tag tagType="blue" size="small" icon={false} rounded>
            Default
          </Tag>
        )}
      </div>
      {frequencyLabel && (
        <span className="t-text-body-sm t-text-text-60">{frequencyLabel}</span>
      )}
      {priceItem.description && (
        <span className="t-text-body-sm t-text-text-30">
          {priceItem.description}
        </span>
      )}
    </div>
  );
};

const DataConsole = ({
  productOrService,
}: {
  productOrService: ProductAndServicesDetail;
}) => {
  const {
    source,
    type,
    product_category_name,
    transaction_category_name,
    linked_price_list = [],
    classification = [],
    default_tax_rate,
  } = productOrService;

  const classificationList = classification.flatMap((group) =>
    group.classes.map((cls) => ({
      groupName: group.name,
      className: cls.name,
      uuid: cls.uuid,
    }))
  );

  return (
    <>
      <div className="t-grid t-grid-cols-2 t-gap-4">
        <InfoItem label="Source">
          <SourceDisplay source={source} />
        </InfoItem>
        <InfoItem label="Type">{PRODUCT_SERVICE_ITEM_TYPE[type]}</InfoItem>
      </div>
      <div className="t-grid t-grid-cols-2 t-gap-4 t-mt-4">
        <InfoItem label="Group">{product_category_name || "-"}</InfoItem>
        <InfoItem label="Income category">
          {transaction_category_name || "-"}
        </InfoItem>
      </div>
      <div className="t-grid t-grid-cols-2 t-gap-4 t-mt-4">
        <InfoItem label="Classification">
          {classificationList.length > 0
            ? classificationList.map((classItem) => (
                <div key={classItem.uuid}>
                  <span className="t-text-text-30 t-text-body-sm">
                    {classItem.groupName}:{" "}
                  </span>
                  {classItem.className}
                </div>
              ))
            : "-"}
        </InfoItem>
        <InfoItem label="Default Tax (%)">
          {default_tax_rate ? `${default_tax_rate.rate}%` : "-"}
        </InfoItem>
      </div>

      <div className="t-relative t-mt-8">
        <Divider />
        <div className="t-absolute t-top-1/2 t-left-1/2 -t-translate-x-1/2 -t-translate-y-1/2 t-flex t-items-center t-gap-1.5 t-px-2 t-py-1 t-rounded-full t-bg-neutral-0">
          <Link size="14" />
          <span className="t-text-body-sm t-text-text-60">Linked prices</span>
        </div>
      </div>

      <div className="t-flex t-flex-col t-gap-3 t-mt-8">
        {linked_price_list.length > 0 ? (
          linked_price_list.map((priceItem) => (
            <PriceCard
              key={priceItem.uuid}
              priceItem={priceItem}
              isDefault={priceItem.is_default}
            />
          ))
        ) : (
          <span className="t-text-body-sm t-text-text-30">
            No linked prices
          </span>
        )}
      </div>
    </>
  );
};

export const ProductOrServiceSlider = ({
  onClose,
}: {
  onClose: () => void;
}) => {
  const { productOrserviceId } = useParams<{ productOrserviceId: string }>();
  const entityId = useCurrentEntityId();
  const { uuid: groupId } = useCurrentGroupContext();
  const editModal = useModal();

  const {
    data: productOrService,
    isSuccess,
    isLoading,
  } = useGetProductAndServiceByIdQuery(
    {
      productId: productOrserviceId,
      entityId,
      groupId,
    },
    {
      skip: !productOrserviceId || !entityId || !groupId,
    }
  );

  const { type = ITEM_TYPES.PRODUCT } = productOrService || {};

  return (
    <>
      <Slider.Root open onOpenChange={onClose}>
        <Slider.Content open useCustomOverlay>
          <Slider.Header>
            <Slider.Title role="heading" aria-level={2}>
              {isLoading ? (
                <div className="t-bg-neutral-10 t-rounded-md t-h-5 t-animate-pulse t-w-60" />
              ) : (
                productOrService?.name
              )}
            </Slider.Title>
            <Slider.Close />
          </Slider.Header>
          <Async.Root
            isLoading={isLoading}
            isSuccess={isSuccess}
            isEmpty={!productOrService}
          >
            <Async.Empty>
              <></>
            </Async.Empty>
            <Async.Success>
              <Slider.Body>
                <Card.Root variant="secondary" borderRounded="lg">
                  <Card.Body>
                    <DataConsole productOrService={productOrService!} />
                  </Card.Body>
                </Card.Root>
              </Slider.Body>
            </Async.Success>
          </Async.Root>
          <Slider.FooterButtonGroup>
            <LockedUi
              feature={WRITE_PERMISSION_KEYS.REVENUE}
              blockedUi={
                <LockedButton
                  text={`Edit ${PRODUCT_SERVICE_ITEM_TYPE[type].toLowerCase()}`}
                />
              }
            >
              <Button
                customType="primary"
                onClick={editModal.open}
                disabled={isLoading}
              >
                Edit {PRODUCT_SERVICE_ITEM_TYPE[type].toLowerCase()}
              </Button>
            </LockedUi>
          </Slider.FooterButtonGroup>
        </Slider.Content>
      </Slider.Root>

      <ProductFormModalRoot
        isOpen={editModal.isOpen}
        close={editModal.close}
        mode="edit"
        itemId={productOrserviceId}
      />
    </>
  );
};
